/**
 * APIService - Base service for API communication with error handling and retry logic
 */

import type { FetchOptions, APIError } from '../../types/api';

/**
 * API error class
 */
export class APIServiceError extends Error {
  constructor(
    message: string,
    public status?: number,
    public code?: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'APIServiceError';
  }
}

/**
 * Base API service with common functionality
 */
export class APIService {
  /**
   * Fetch with retry logic and timeout
   * 
   * @param url - Request URL
   * @param options - Fetch options with retry configuration
   * @returns Response
   */
  protected async fetchWithRetry(
    url: string,
    options: FetchOptions = {}
  ): Promise<Response> {
    const {
      retries = 3,
      retryDelay = 1000,
      timeout = 30000,
      ...fetchOptions
    } = options;

    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= retries; attempt++) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      // Link a caller-provided signal (e.g. a Cancel button) to this request so
      // an external abort cancels the in-flight fetch too. The internal
      // controller still owns the timeout.
      const externalSignal = fetchOptions.signal ?? null;
      const onExternalAbort = () => controller.abort();
      if (externalSignal) {
        if (externalSignal.aborted) controller.abort();
        else externalSignal.addEventListener('abort', onExternalAbort);
      }

      try {
        const response = await fetch(url, {
          ...fetchOptions,
          signal: controller.signal,
        });

        // Don't retry on client errors (4xx) except 429 (rate limit)
        if (!response.ok && response.status >= 400 && response.status < 500 && response.status !== 429) {
          throw await this.handleErrorResponse(response);
        }

        // Retry on server errors (5xx) or rate limit (429)
        if (!response.ok && (response.status >= 500 || response.status === 429)) {
          if (attempt < retries) {
            await this.delay(retryDelay * Math.pow(2, attempt)); // Exponential backoff
            continue;
          }
          throw await this.handleErrorResponse(response);
        }

        return response;
      } catch (error) {
        lastError = error as Error;

        // Don't retry on abort or network errors on last attempt
        if (attempt === retries) {
          throw this.handleFetchError(error);
        }

        // Retry on network errors
        if (error instanceof TypeError || (error as Error).name === 'AbortError') {
          await this.delay(retryDelay * Math.pow(2, attempt));
          continue;
        }

        // Don't retry on API errors
        if (error instanceof APIServiceError) {
          throw error;
        }
      } finally {
        clearTimeout(timeoutId);
        if (externalSignal) externalSignal.removeEventListener('abort', onExternalAbort);
      }
    }

    throw lastError || new APIServiceError('Request failed after retries');
  }

  /**
   * Handle error response from API
   * 
   * @param response - Error response
   * @returns APIServiceError
   */
  private async handleErrorResponse(response: Response): Promise<APIServiceError> {
    let errorData: APIError | null = null;

    try {
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        errorData = await response.json();
      }
    } catch {
      // Ignore JSON parse errors
    }

    const message = errorData?.message || `Request failed with status ${response.status}`;
    return new APIServiceError(
      message,
      response.status,
      errorData?.code,
      errorData?.details
    );
  }

  /**
   * Handle fetch errors (network, timeout, etc.)
   * 
   * @param error - Error object
   * @returns APIServiceError
   */
  private handleFetchError(error: unknown): APIServiceError {
    if (error instanceof APIServiceError) {
      return error;
    }

    const err = error as Error;

    if (err.name === 'AbortError') {
      return new APIServiceError('Request timeout');
    }

    if (error instanceof TypeError) {
      return new APIServiceError('Network error - please check your connection');
    }

    return new APIServiceError(err.message || 'Unknown error occurred');
  }

  /**
   * Delay helper for retry logic
   * 
   * @param ms - Milliseconds to delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Get authorization header with token
   * 
   * @returns Authorization header object
   */
  protected getAuthHeader(): Record<string, string> {
    // Use ID token instead of access token - ID token contains user profile claims (email, name, etc.)
    // that are required by the backend lambdas
    const token = localStorage.getItem('idToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Check if user is authenticated
   * 
   * @returns True if authenticated
   */
  protected isAuthenticated(): boolean {
    return !!localStorage.getItem('accessToken');
  }
}
